
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { fetchModules, createModule, updateModule } from '@/services/modules/moduleService';
import { fetchModuleLearningObjectives } from '@/services/modules/objectiveService';
import { LearningModule, TaxonomyLevel } from '@/types/supabase';
import ModuleForm, { ModuleFormValues } from './ModuleForm';
import LessonBrowser from './LessonBrowser';
import LearningObjectiveManager from './LearningObjectiveManager';
import ModuleStatus from './ModuleStatus';
import ModuleComments from './ModuleComments';
import { useToast } from '@/hooks/use-toast';

interface ModuleEditorProps {
  moduleId?: string;
  onModuleSelected?: (module: LearningModule) => void;
  onModuleCreated?: (module: LearningModule) => void;
}

const ModuleEditor: React.FC<ModuleEditorProps> = ({ moduleId, onModuleSelected, onModuleCreated }) => {
  const [selectedModule, setSelectedModule] = useState<LearningModule | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: modules, isLoading } = useQuery({
    queryKey: ['modules'],
    queryFn: fetchModules,
  });

  const createModuleMutation = useMutation({
    mutationFn: createModule,
    onSuccess: (newModule) => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      toast({ 
        title: 'Module created', 
        description: `The module "${newModule.title}" has been created successfully.` 
      });
      setSelectedModule(newModule);
      if (onModuleCreated) onModuleCreated(newModule);
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: 'Failed to create module. Please try again.', 
        variant: 'destructive' 
      });
      console.error('Error creating module:', error);
    }
  });

  const updateModuleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: Partial<LearningModule> }) => 
      updateModule(id, data),
    onSuccess: (updatedModule) => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      toast({ 
        title: 'Module updated', 
        description: `The module "${updatedModule.title}" has been updated successfully.` 
      });
      setSelectedModule(updatedModule);
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: 'Failed to update module. Please try again.', 
        variant: 'destructive' 
      });
      console.error('Error updating module:', error);
    }
  });

  // Query to fetch learning objectives for the selected module
  const { data: objectives } = useQuery({
    queryKey: ['moduleObjectives', selectedModule?.id],
    queryFn: () => selectedModule?.id ? fetchModuleLearningObjectives(selectedModule.id) : Promise.resolve([]),
    enabled: !!selectedModule?.id,
  });

  useEffect(() => {
    if (moduleId && modules) {
      const module = modules.find((m) => m.id === moduleId);
      setSelectedModule(module || null);
    } else {
      setSelectedModule(null);
    }
  }, [moduleId, modules]);

  const handleModuleSelect = (module: LearningModule) => {
    setSelectedModule(module);
    if (onModuleSelected) onModuleSelected(module);
  };

  const handleCreateSubmit = (values: ModuleFormValues) => {
    // Transform taxonomy_levels array to proper format
    const data = {
      ...values,
      taxonomy_level: values.taxonomy_levels as TaxonomyLevel[] || []
    };
    
    // Remove the taxonomy_levels field before sending to API
    delete (data as any).taxonomy_levels;
    
    createModuleMutation.mutate(data as any);
  };

  const handleUpdateSubmit = (values: ModuleFormValues) => {
    if (selectedModule) {
      // Transform taxonomy_levels array to proper format
      const data = {
        ...values,
        taxonomy_level: values.taxonomy_levels as TaxonomyLevel[] || []
      };
      
      // Remove the taxonomy_levels field before sending to API
      delete (data as any).taxonomy_levels;

      updateModuleMutation.mutate({
        id: selectedModule.id,
        data: data as any
      });
    }
  };

  const getDefaultValues = (module?: LearningModule | null): ModuleFormValues => {
    return {
      title: module?.title || '',
      description: module?.description || '',
      difficulty_level: module?.difficulty_level || 'beginner',
      order_position: module?.order_position || 1,
      phase: (module?.phase as any) || 'Foundation',
      week: module?.week || 1,
      track: (module?.track as any) || 'Required',
      estimated_hours: module?.estimated_hours || 2,
      taxonomy_levels: module?.taxonomy_level as string[] || []
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium">Module Editor</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-md font-semibold mb-2">Select Existing Module</h3>
          <ul className="space-y-2">
            {modules && modules.map((module) => (
              <li
                key={module.id}
                className={`p-3 rounded-md cursor-pointer hover:bg-accent ${selectedModule?.id === module.id ? 'bg-secondary' : ''}`}
                onClick={() => handleModuleSelect(module)}
              >
                {module.title}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-md font-semibold mb-2">
            {selectedModule ? 'Edit Module' : 'Create New Module'}
          </h3>
          <ModuleForm 
            defaultValues={getDefaultValues(selectedModule)}
            onSubmit={selectedModule ? handleUpdateSubmit : handleCreateSubmit}
            isSubmitting={createModuleMutation.isPending || updateModuleMutation.isPending}
            submitLabel={selectedModule ? 'Update Module' : 'Create Module'}
          />
        </div>
      </div>

      {selectedModule && (
        <div className="space-y-4">
          <h3 className="text-md font-semibold">Module Details</h3>
          <p>Title: {selectedModule.title}</p>
          <p>Description: {selectedModule.description || 'No description'}</p>
          <LearningObjectiveManager moduleId={selectedModule.id} />
          <ModuleStatus moduleId={selectedModule.id} />
          <ModuleComments moduleId={selectedModule.id} />
          <LessonBrowser moduleId={selectedModule.id} moduleName={selectedModule.title} />
        </div>
      )}
    </div>
  );
};

export default ModuleEditor;
